// Canonical display state for audits — single source of truth for the
// Progress + Stage + Date + CTA logic used by Card view, Table view, and
// the Audit Detail page. Keep in sync with the Status Wording spec.

// Progress badge palette (semantic tokens rendered inline so we don't fight
// against Tailwind's JIT purge for dynamic class names).
const PROGRESS = {
  planned:       { label: "Planned",        bg: "#E2E8F0", fg: "#334155", border: "#CBD5E1" }, // slate / grey
  in_progress:   { label: "In Progress",    bg: "#FFEDD5", fg: "#9A3412", border: "#FDBA74" }, // orange
  pending_review:{ label: "Pending Review", bg: "#FEF3C7", fg: "#78350F", border: "#FCD34D" }, // yellow
  approved:      { label: "Approved",       bg: "#D1FAE5", fg: "#065F46", border: "#6EE7B7" }, // green
  closed:        { label: "Closed",         bg: "#1E3A8A", fg: "#FFFFFF", border: "#1E40AF" }, // dark blue
  rejected:      { label: "Rejected",       bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5" }, // red
};

const STAGE = {
  pre_audit:  { label: "Pre-Audit",   short: "→ Pre-Audit",   arrow: "→", tone: "sky" },
  post_audit: { label: "Post-Audit",  short: "→ Post-Audit",  arrow: "→", tone: "indigo" },
  completed:  { label: "Completed",   short: "✓ Completed",   arrow: "✓", tone: "emerald" },
};

// Given an audit document, return the canonical display state per the spec:
//   { progress: { key, label, bg, fg, border },
//     stage:    { key, label, short, tone },
//     date:     { key: 'pre_audit_date'|'post_audit_date'|null, value: iso|null, label },
//     subText:  string | null,
//     cta:      { key, label, icon: 'play'|'eye'|'start-post' } }
//
// Handles this decision matrix (spec):
//   phase=pre_audit   status=planned      → Planned      → Pre-Audit  · pre_date  · Open
//   phase=pre_audit   status=in_progress  → In Progress  → Pre-Audit  · pre_date  · Continue
//   phase=pre_audit   status=submitted    → Pending Rev. → Pre-Audit  · pre_date  · View
//   phase=pre_audit   status=approved     → Approved     → Post-Audit · post_date · Start Post-Audit  (transitional)
//   phase=post_audit  status=planned      → Planned      → Post-Audit · post_date · Start Post-Audit
//   phase=post_audit  status=in_progress  → In Progress  → Post-Audit · post_date · Continue Post-Audit
//   phase=post_audit  status=submitted    → Pending Rev. → Post-Audit · post_date · View
//   phase=post_audit  status=approved     → Closed       → Completed  · —        · View Report
//   phase=post_audit  status=closed       → Closed       → Completed  · —        · View Report
//   any               status=rejected     → Rejected     (current stage) · current date · Continue
export function getAuditDisplayState(audit) {
  if (!audit) {
    return {
      progress: PROGRESS.planned,
      stage: STAGE.pre_audit,
      date: { key: null, value: null, label: "Date" },
      subText: null,
      cta: { key: "open", label: "Open", icon: "play" },
    };
  }

  const phase = audit.phase || audit.stage || "pre_audit";
  const status = audit.status || "planned";
  const preDate = audit.pre_audit_date || audit.planned_date || null;
  const postDate = audit.post_audit_date || null;

  // --- Rejected: keep on current phase/date but flag red + Continue CTA ---
  if (status === "rejected") {
    return {
      progress: PROGRESS.rejected,
      stage: phase === "post_audit" ? STAGE.post_audit : STAGE.pre_audit,
      date: phase === "post_audit"
        ? { key: "post_audit_date", value: postDate, label: "Post-Audit Date" }
        : { key: "pre_audit_date",  value: preDate,  label: "Pre-Audit Date" },
      subText: "Rejected — please revise & resubmit",
      cta: { key: "continue", label: "Continue", icon: "play" },
    };
  }

  // --- Post-Audit closed / approved → Closed (dark blue) with Completed stage ---
  if (phase === "post_audit" && (status === "approved" || status === "closed")) {
    return {
      progress: PROGRESS.closed,
      stage: STAGE.completed,
      date: { key: null, value: null, label: "—" },
      subText: "Audit closed",
      cta: { key: "view-report", label: "View Report", icon: "eye" },
    };
  }

  // --- Pre-Audit approved → transitional row (display as APPROVED + Post-Audit stage) ---
  if (phase === "pre_audit" && status === "approved") {
    return {
      progress: PROGRESS.approved,
      stage: STAGE.post_audit,
      date: { key: "post_audit_date", value: postDate, label: "Post-Audit Date" },
      subText: "Pre-Audit approved · Proceed to Post-Audit",
      cta: { key: "start-post", label: "Start Post-Audit", icon: "start-post" },
    };
  }

  // --- Post-Audit stage rows ---
  if (phase === "post_audit") {
    if (status === "submitted") {
      return {
        progress: PROGRESS.pending_review,
        stage: STAGE.post_audit,
        date: { key: "post_audit_date", value: postDate, label: "Post-Audit Date" },
        subText: null,
        cta: { key: "view", label: "View", icon: "eye" },
      };
    }
    if (status === "in_progress") {
      return {
        progress: PROGRESS.in_progress,
        stage: STAGE.post_audit,
        date: { key: "post_audit_date", value: postDate, label: "Post-Audit Date" },
        subText: null,
        cta: { key: "continue-post", label: "Continue Post-Audit", icon: "play" },
      };
    }
    // planned
    return {
      progress: PROGRESS.planned,
      stage: STAGE.post_audit,
      date: { key: "post_audit_date", value: postDate, label: "Post-Audit Date" },
      subText: null,
      cta: { key: "start-post", label: "Start Post-Audit", icon: "start-post" },
    };
  }

  // --- Pre-Audit stage rows ---
  if (status === "submitted") {
    return {
      progress: PROGRESS.pending_review,
      stage: STAGE.pre_audit,
      date: { key: "pre_audit_date", value: preDate, label: "Pre-Audit Date" },
      subText: null,
      cta: { key: "view", label: "View", icon: "eye" },
    };
  }
  if (status === "in_progress") {
    return {
      progress: PROGRESS.in_progress,
      stage: STAGE.pre_audit,
      date: { key: "pre_audit_date", value: preDate, label: "Pre-Audit Date" },
      subText: null,
      cta: { key: "continue", label: "Continue", icon: "play" },
    };
  }
  // Pre-audit planned (default)
  return {
    progress: PROGRESS.planned,
    stage: STAGE.pre_audit,
    date: { key: "pre_audit_date", value: preDate, label: "Pre-Audit Date" },
    subText: null,
    cta: { key: "open", label: "Open", icon: "play" },
  };
}

// Which filter tab does a given audit belong to? Used by the top-level tab
// bar in AuditsListPage to count and filter rows.
//   pending_review = status === 'submitted'
//   approved       = phase='pre_audit' && status='approved' (post-audit-ready)
//   closed         = phase='post_audit' && status ∈ ('approved','closed')
export function filterTabKey(audit) {
  if (!audit) return "planned";
  const status = audit.status || "planned";
  const phase = audit.phase || audit.stage || "pre_audit";
  if (status === "submitted") return "pending_review";
  if (status === "in_progress" || status === "rejected") return "in_progress";
  if (phase === "post_audit" && (status === "approved" || status === "closed")) return "closed";
  if (phase === "pre_audit" && status === "approved") return "approved";
  return "planned";
}

export const AUDIT_FILTER_TABS = [
  { key: "all",            label: "All",            statusParam: "all" },
  { key: "planned",        label: "Planned",        statusParam: "planned" },
  { key: "in_progress",    label: "In Progress",    statusParam: "in_progress" },
  { key: "pending_review", label: "Pending Review", statusParam: "submitted" },
  { key: "approved",       label: "Approved",       statusParam: "approved" }, // pre-audit approved (transitional)
  { key: "closed",         label: "Closed",         statusParam: "closed" },   // fully done
];
